import loadingProcess from '../views/schedule/init/index';
import { loadDataForCalendar } from '../views/schedule/calendar-helpers';
import { configureColumns } from '../views/schedule/column-helpers';
import { ActivitiesStorage } from '../assets/js/gantt_storage/activities';
import { LinksStorage } from '../assets/js/gantt_storage/links';
import { dynamicSort } from '../utils/';
import i18n from '../i18n';
import { setCustomHour } from '../redux/slices/calendarCustomHourSlice';

/**
 * Custom hook para manejar la carga y gestión de la instancia Gantt
 */
export const useGanttLoader = () => {
  /**
   * Inicializa y configura una nueva instancia de Gantt
   */
  const initializeGantt = async ({
    sector,
    activities,
    links,
    calendars,
    visualizationConfig,
    dispatch
  }) => {
    window.loadingGantt = true;

    // 1. Crear instancia
    const newGanttInstance = loadingProcess.getNewGanttLibInstance(
      sector,
      calendars,
      i18n.t
    );
    window.to_use_react_gantt = newGanttInstance;
    // 2. Inicializar con datos
    loadingProcess.initGantt(
      newGanttInstance,
      { data: activities, links },
      visualizationConfig,
      i18n.t,
      sector
    );

    // Configurar propiedades básicas
    newGanttInstance.initAlreadyDid = true;
    newGanttInstance.clearStacks = false;

    // 3. Aplicar correcciones de Primavera si es necesario
    if (sector?.update_duration_for_primavera_for_end_date) {
      activities?.forEach((data) => {
        const calendar = newGanttInstance.getCalendar(data.calendar_id);
        if (calendar) {
          const duration = calendar.calculateDuration(
            new Date(data.non_parsed_original_start_date),
            new Date(data.non_parsed_original_end_date)
          );
          data.duration = duration;
          data.duration_milestone_bugged = duration;
          data.for_disable_milestone_duration = duration;
          data.non_parsed_original_duration = duration;
          data.old_duration = duration;
          data.old_duration_registered = duration;
        }
      });
    }

    // 4. Cargar datos del calendario
    loadDataForCalendar(newGanttInstance, calendars, {
      baselineSaveInProcess: false
    });

    // 5. Configurar horas según turnos
    setHoursAccordingShifts(calendars, newGanttInstance, dispatch);

    // 6. Configurar columnas
    configureColumns(newGanttInstance, i18n.t, dispatch);

    // 7. Inicializar almacenamiento
    ActivitiesStorage.init(newGanttInstance);
    LinksStorage.init(newGanttInstance);

    // 8. Configurar columnas de grid
    if (
      newGanttInstance?.userPreferenceTableGantt?.gridTable &&
      newGanttInstance?.userPreferenceTableGantt?.gridTable?.length
    ) {
      newGanttInstance.userPreferenceTableGantt.gridTable.forEach((column) => {
        const width =
          !column.width || column.width === '*' ? '100' : column.width;

        newGanttInstance.getGridColumn(column.name).width = width;
      });
    }

    // 9. Configuración final
    window.loadingGantt = false;
    newGanttInstance.firstLoad = true;
    newGanttInstance.adjustGridWidth();
    newGanttInstance.shouldAdjustWorkingHours = false;

    // Devolver la instancia para uso inmediato
    return newGanttInstance;
  };

  const setHoursAccordingShifts = (calendars, ganttInstance, dispatch) => {
    if (!calendars) return;
    if (Object.keys(calendars).length !== 0) {
      const calendarDefault =
        calendars && calendars.find((el) => el.is_default);
      if (calendarDefault) {
        const shifts = calendarDefault.shifts;
        const shiftOrdered = shifts.sort(dynamicSort('correlative_id', true));

        /** get shifts */
        const firstShift = shiftOrdered[0].shift_string;
        const lastShift = shiftOrdered[shiftOrdered.length - 1].shift_string;

        /** calculate ini */
        const shiftArr = firstShift.split('-');
        const firstHourArr = shiftArr[0].split(',');

        /** calculate end */
        const shiftArrEnd = lastShift.split('-');
        const lastHourArr = shiftArrEnd[1].split(',');

        /** calculate min hour of the last shift */
        let lastHourMin = 99;
        firstHourArr.map((el) => {
          if (parseInt(el) < parseInt(lastHourMin)) {
            lastHourMin = el;
          }
        });

        /** calculate max hour of the last shift */
        let lastHourMax = 0;
        lastHourArr.map((el) => {
          if (parseInt(el) > parseInt(lastHourMax)) {
            lastHourMax = el;
          }
        });

        if (lastHourMin && lastHourMax) {
          const customHours = {
            startHour: lastHourMin,
            endHour: lastHourMax
          };
          ganttInstance.customHours = customHours;
          dispatch(setCustomHour(customHours));
        }
      }
    }
  };

  return {
    initializeGantt
  };
};
